import { type ComponentProps, type ReactNode } from "react";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal } from "antd";

import { RVButton } from "../RVButton/RVButton";

import type { SizeType } from "antd/lib/config-provider/SizeContext";

import styles from "./RVConfirmationTriggerModalButton.module.scss";

/** Presentation props forwarded to the trigger `RVButton` (for example pill `shape` and raised shadow). */
type TriggerButtonPresentationProps = Pick<ComponentProps<typeof RVButton>, "className" | "shape">;

export type RVConfirmationTriggerModalButtonProps = {
    /** Optional button type; currently only "primary" is supported */
    type?: "primary";
    /** Button size (small, middle, large). Defaults to "middle" */
    size?: SizeType;
    /** Whether the trigger button is disabled */
    disabled?: boolean;
    /** Whether the OK/confirm button inside the dialog is disabled */
    okDisabled?: boolean;
    /** Whether the trigger button shows a loading spinner */
    loading: boolean;
    /** Async action to invoke on dialog confirmation */
    handleMethod: () => Promise<void>;
    /** When true, styles the OK button in red (destructive action) */
    danger?: boolean;
    /** Label text for the trigger button */
    buttonText: string;
    /** Title shown in the confirmation dialog */
    title: string;
    /** Body content of the confirmation dialog */
    content: ReactNode;
    /** Label for the OK/confirm button in the dialog */
    confirmText: string;
    /**
     * Text shown on the OK button while the async action runs.
     * @default "Loading..."
     */
    okLoading?: string;
    /** Optional icon rendered inside the trigger button */
    icon?: JSX.Element;
    /** data-testid for the trigger button */
    mainbuttonId?: string;
    /** data-testid for the dialog OK button */
    okButtonId?: string;
} & TriggerButtonPresentationProps;

/**
 * RVConfirmationTriggerModalButton renders a button that, on click, opens an imperative
 * Ant Design `Modal.confirm` dialog. On confirmation it runs the `handleMethod` promise
 * and destroys the dialog when it resolves.
 *
 * Use this component for lightweight, one-off destructive or irreversible actions (e.g.
 * Delete, Stop, Publish). For rich modal content with custom footers, use `RVTriggerModal`.
 *
 * @example
 * // Destructive action (danger variant)
 * <RVConfirmationTriggerModalButton
 *   type="primary"
 *   danger
 *   buttonText="Delete IVR"
 *   title="Delete this IVR?"
 *   content="This action cannot be undone."
 *   confirmText="Yes, Delete IVR"
 *   loading={false}
 *   handleMethod={handleDelete}
 * />
 *
 * @example
 * // Non-destructive confirmation
 * <RVConfirmationTriggerModalButton
 *   type="primary"
 *   buttonText="Save as Live"
 *   title="Save as Live?"
 *   content="This will activate the journey for all users."
 *   confirmText="Save as Live"
 *   loading={isSaving}
 *   handleMethod={handleSave}
 * />
 */
export function RVConfirmationTriggerModalButton({
    type,
    disabled,
    loading,
    size = "middle",
    handleMethod,
    danger,
    buttonText,
    title,
    content,
    confirmText,
    okLoading = "Loading...",
    icon,
    okDisabled,
    mainbuttonId,
    okButtonId,
    className,
    shape,
}: RVConfirmationTriggerModalButtonProps): JSX.Element {
    const okButtonProps = {
        disabled: okDisabled,
        className: danger ? styles.dangerButton : "",
        dataTestId: okButtonId,
    };

    function confirmChange(): void {
        const confirm = Modal.confirm({
            title,
            icon: <ExclamationCircleOutlined />,
            content,
            className: styles.confirmModal,
            okText: confirmText,
            okButtonProps,
            async onOk() {
                confirm.update((prevConfig: any) => ({
                    ...prevConfig,
                    okText: okLoading,
                    cancelButtonProps: {
                        disabled: true,
                    },
                }));
                try {
                    await handleMethod();
                } finally {
                    confirm.destroy();
                }
            },
        });
    }

    return (
        <RVButton
            data-testid={mainbuttonId}
            type={type}
            size={size}
            loading={loading}
            disabled={disabled}
            icon={icon}
            danger={danger}
            className={className}
            shape={shape}
            onClick={confirmChange}
        >
            <span>{buttonText}</span>
        </RVButton>
    );
}
